/** Paleta por defecto para cabeceras de grupo en reportes */
export const REPORT_GROUP_COLORS = [
  "#dbeafe",
  "#dcfce7",
  "#fef3c7",
  "#fce7f3",
  "#e0e7ff",
  "#ffedd5",
];

export function mergeMemberKey(mergeId) {
  return `merge:${mergeId}`;
}

export function newLayoutId() {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * @param {object} layout
 * @param {string[]} visibleFieldKeys claves de columnas visibles (sin merges)
 */
export function normalizeColumnLayout(layout, visibleFieldKeys = []) {
  const keys = new Set(visibleFieldKeys);
  const merges = (Array.isArray(layout?.merges) ? layout.merges : [])
    .map((m) => ({
      id: m.id || newLayoutId(),
      members: (Array.isArray(m.members) ? m.members : []).filter((k) =>
        keys.has(k)
      ),
      mode: m.mode === "sum" ? "sum" : "stack",
      label: typeof m.label === "string" ? m.label.trim() : "",
    }))
    .filter((m) => m.members.length >= 2);

  const displayKeys = buildDisplayColumns(
    visibleFieldKeys.map((k) => ({ key: k })),
    { merges, groups: [] }
  ).map((c) => c.key);

  const validDisplay = new Set(displayKeys);

  const groups = (Array.isArray(layout?.groups) ? layout.groups : [])
    .map((g) => ({
      id: g.id || newLayoutId(),
      title: typeof g.title === "string" ? g.title.trim() : "",
      color:
        typeof g.color === "string" && g.color.trim()
          ? g.color.trim()
          : REPORT_GROUP_COLORS[0],
      members: (Array.isArray(g.members) ? g.members : []).filter((k) =>
        validDisplay.has(k)
      ),
    }))
    .filter((g) => g.members.length >= 1 && g.title);

  return { merges, groups };
}

/**
 * @param {Array<{key:string}>} visibleFields
 * @param {{ merges?: object[], groups?: object[] }} layout
 */
export function buildDisplayColumns(visibleFields, layout = {}) {
  const merges = layout.merges || [];
  const mergeByMember = new Map();
  merges.forEach((m) => {
    (m.members || []).forEach((k) => mergeByMember.set(k, m));
  });

  const skipped = new Set();
  const result = [];

  (visibleFields || []).forEach((field) => {
    if (!field || skipped.has(field.key)) return;

    const merge = mergeByMember.get(field.key);
    if (merge) {
      const fields = (merge.members || [])
        .map((k) => visibleFields.find((f) => f.key === k))
        .filter(Boolean);
      fields.forEach((f) => skipped.add(f.key));
      if (fields.length < 2) {
        fields.forEach((f) => {
          result.push({ displayType: "field", key: f.key, field: f });
        });
        return;
      }
      result.push({
        displayType: "merged",
        key: mergeMemberKey(merge.id),
        mergeId: merge.id,
        fields,
        mode: merge.mode === "sum" ? "sum" : "stack",
        label: merge.label || "",
      });
    } else {
      result.push({ displayType: "field", key: field.key, field });
    }
  });

  return result;
}

/**
 * @param {ReturnType<buildDisplayColumns>} displayColumns
 * @param {{ groups?: object[] }} layout
 * @returns {Array<{title:string|null,colspan:number,color:string|null,groupId?:string}>|null}
 */
export function buildGroupHeaderRow(displayColumns, layout = {}) {
  const groups = layout.groups || [];
  if (!groups.length || !displayColumns?.length) return null;

  const keyToGroup = new Map();
  groups.forEach((g) => {
    (g.members || []).forEach((k) => keyToGroup.set(k, g));
  });

  const cells = [];
  let i = 0;
  while (i < displayColumns.length) {
    const colKey = displayColumns[i].key;
    const g = keyToGroup.get(colKey);
    if (!g) {
      cells.push({ title: null, colspan: 1, color: null });
      i += 1;
      continue;
    }
    let span = 0;
    while (i + span < displayColumns.length) {
      const k = displayColumns[i + span].key;
      if (keyToGroup.get(k) !== g) break;
      span += 1;
    }
    cells.push({
      title: g.title,
      colspan: span,
      color: g.color || REPORT_GROUP_COLORS[0],
      groupId: g.id,
    });
    i += span;
  }
  return cells;
}

export function isNumericColumnField(field) {
  if (!field) return false;
  const t = field.subFieldType || field.type;
  return t === "number" || t === "currency";
}

export function canSumMergedFields(fields) {
  return (fields || []).every((f) => isNumericColumnField(f));
}
